/**
 * Hintergruende, Schrift- und Akkordfarben.
 *
 * Ergaenzt die Themes (Papier/Dunkel), ersetzt sie nicht: jede Auswahl hier
 * hat eine helle und eine dunkle Fassung. Wer zwischen Hell und Dunkel
 * wechselt, behaelt seinen Hintergrund und seine Farben – nur eben in der
 * passenden Fassung.
 *
 * Alles ist reines CSS (Verlaeufe und SVG-Filter als Data-URL): keine
 * Bilddateien, laeuft offline, bleibt in jeder Aufloesung scharf.
 *
 * NEUEN HINTERGRUND ODER FARBE ANLEGEN
 *   Einen Eintrag in BACKGROUNDS, INKS oder CHORDS ergaenzen – fertig. Die
 *   Einstellungen zeigen ihn automatisch als Kachel an.
 *
 * Technisch werden die Werte als CSS-Variablen direkt am <html>-Element
 * gesetzt. Sie gewinnen damit gegen die Theme-Dateien, und alles, was mit
 * var(--c-bg), var(--c-ink) usw. arbeitet, zieht ohne Umbau mit.
 */

import { mixColor } from './mix.ts';

export type Tone = 'light' | 'dark';

/** Eine Fassung eines Hintergrunds. */
interface BackgroundTone {
  /** Grundfarbe; auch dort, wo der Hintergrund flach bleibt (Buehne, Flaechen). */
  bg: string;
  /** Das eigentliche Motiv, ein CSS background-image. */
  art: string;
  /** Karten und Leisten, passend zum Motiv getoent. */
  surface: string;
  /** Eingabefelder. */
  surface2: string;
  border: string;
  /** Notizblock-Linien hinter dem Lead Sheet. */
  rule: string;
  /** Statusleiste auf dem iPad. */
  themeColor: string;
}

export interface BackgroundDef {
  id: string;
  name: string;
  /** Gruppe in der Auswahl, siehe BACKGROUND_GROUPS. */
  group: BackgroundGroupId;
  light: BackgroundTone;
  dark: BackgroundTone;
}

/**
 * Gruppen der Auswahl. Die Einstellungen zeigen immer nur eine Gruppe –
 * so wird man beim Oeffnen nicht mit allen Mustern auf einmal bedrängt.
 */
export const BACKGROUND_GROUPS = [
  { id: 'white', name: 'Weiß' },
  { id: 'pastel', name: 'Pastell' },
  { id: 'nature', name: 'Stoff & Stein' },
  { id: 'mood', name: 'Stimmung' },
] as const;

export type BackgroundGroupId = (typeof BACKGROUND_GROUPS)[number]['id'];

/** Eine Farbe mit heller und dunkler Fassung. */
export interface ColorDef {
  id: string;
  name: string;
  light: string;
  dark: string;
}

/** Schriftfarbe mit den zwei abgeleiteten Stufen fuer Nebentext. */
export interface InkDef {
  id: string;
  name: string;
  light: { ink: string; soft: string; faint: string };
  dark: { ink: string; soft: string; faint: string };
}

/** Sonderwert: nichts ueberschreiben, das Theme bestimmt. */
export const THEME_DEFAULT = 'theme';

/** Sonderwert: eigenes Hintergrundbild (Appearance.customImage). */
export const CUSTOM_BACKGROUND = 'custom';

/** Voreinstellung fuer den Schleier ueber dem eigenen Bild. */
export const DEFAULT_IMAGE_VEIL = 0.3;

/* ------------------------------------------------------------ Bausteine */

function svg(body: string, w = 600, h = 600): string {
  const markup = `<svg xmlns='http://www.w3.org/2000/svg' width='${w}' height='${h}' preserveAspectRatio='none'>${body}</svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(markup)}")`;
}

/** Hex-Farbe als Anteile 0..1 fuer eine feColorMatrix. */
function rgb(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16);
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255].map((v) =>
    Number(v.toFixed(3)),
  ) as [number, number, number];
}

/**
 * Filter, der aus dem roten Kanal eines Rauschens die Deckkraft macht und
 * alles in einer Farbe einfaerbt.
 */
function tint(color: string, slope: number, offset: number): string {
  const [r, g, b] = rgb(color);
  return `<feColorMatrix type='matrix' values='0 0 0 0 ${r}  0 0 0 0 ${g}  0 0 0 0 ${b}  ${slope} 0 0 0 ${offset}'/>`;
}

/** Feines Korn. `alpha` = Staerke; auf dunklem Grund hell (`#ffffff`). */
function grain(alpha: number, color = '#000000', freq = 0.9): string {
  return svg(
    `<filter id='g' x='0' y='0' width='100%' height='100%'>` +
      `<feTurbulence type='fractalNoise' baseFrequency='${freq}' numOctaves='3' stitchTiles='stitch'/>` +
      tint(color, alpha * 2.5, -alpha * 0.9) +
      `</filter><rect width='100%' height='100%' filter='url(#g)'/>`,
    240,
    240,
  );
}

/**
 * Marmor: "turbulence" (nicht fractalNoise) erzeugt scharfe Grate. Wo der
 * Wert gegen null laeuft, liegen die Adern; darunter eine weiche Wolke.
 */
function marble(vein: string, veinAlpha: number, cloud: string, cloudAlpha: number, seed: number): string {
  return svg(
    `<filter id='c' x='0' y='0' width='100%' height='100%'>` +
      `<feTurbulence type='fractalNoise' baseFrequency='0.0032' numOctaves='4' seed='${seed + 11}'/>` +
      tint(cloud, 1, 0) +
      `<feComponentTransfer><feFuncA type='table' tableValues='0 0 0 ${cloudAlpha * 0.5} ${cloudAlpha}'/></feComponentTransfer>` +
      `</filter>` +
      `<filter id='v' x='0' y='0' width='100%' height='100%'>` +
      `<feTurbulence type='turbulence' baseFrequency='0.0019 0.0048' numOctaves='6' seed='${seed}'/>` +
      tint(vein, 1, 0) +
      `<feComponentTransfer><feFuncA type='table' tableValues='${veinAlpha} ${veinAlpha * 0.45} ${veinAlpha * 0.12} ${veinAlpha * 0.03} 0 0 0 0 0 0 0 0'/></feComponentTransfer>` +
      `</filter>` +
      `<rect width='100%' height='100%' filter='url(#c)'/>` +
      `<rect width='100%' height='100%' filter='url(#v)'/>`,
    1400,
    1400,
  );
}

/** Leinenbindung: feine Kett- und Schussfaeden. */
function weave(line: string, alpha: number): string {
  const c = (a: number) => line.replace('A', String(a));
  return [
    `repeating-linear-gradient(0deg, ${c(alpha)} 0 1px, transparent 1px 3px)`,
    `repeating-linear-gradient(90deg, ${c(alpha * 0.8)} 0 1px, transparent 1px 4px)`,
  ].join(', ');
}

/**
 * Pastell: heller, weicher Grund mit zwei Farbwolken. Die dunkle Fassung
 * ist dieselbe Farbe als tiefe, gedaempfte Nacht mit einem Schimmer.
 */
function pastel(
  id: string,
  name: string,
  light: { bg: string; a: string; b: string },
  dark: { bg: string; glow: string },
): BackgroundDef {
  return {
    id,
    name,
    group: 'pastel',
    light: {
      bg: light.bg,
      art: [
        `radial-gradient(ellipse 80% 65% at 12% 0%, ${light.a}, transparent 70%)`,
        `radial-gradient(ellipse 85% 70% at 100% 100%, ${light.b}, transparent 72%)`,
        'radial-gradient(ellipse 120% 80% at 60% 40%, rgba(255,255,255,0.45), transparent 70%)',
        grain(0.03),
      ].join(', '),
      surface: mixColor(light.bg, '#ffffff', 0.55),
      surface2: mixColor(light.bg, '#ffffff', 0.8),
      border: mixColor(light.bg, '#000000', 0.09),
      rule: mixColor(light.bg, '#000000', 0.07),
      themeColor: light.bg,
    },
    dark: {
      bg: dark.bg,
      art: [
        `radial-gradient(ellipse 100% 70% at 20% 0%, ${dark.glow}, transparent 68%)`,
        'radial-gradient(ellipse 90% 60% at 100% 100%, rgba(0,0,0,0.35), transparent 70%)',
        grain(0.035, '#ffffff'),
      ].join(', '),
      surface: mixColor(dark.bg, '#ffffff', 0.05),
      surface2: mixColor(dark.bg, '#ffffff', 0.09),
      border: mixColor(dark.bg, '#ffffff', 0.15),
      rule: mixColor(dark.bg, '#ffffff', 0.1),
      themeColor: dark.bg,
    },
  };
}

/* --------------------------------------------------------- Hintergruende */

export const BACKGROUNDS: BackgroundDef[] = [
  /*
   * Weisse Hintergruende. Hell sind sie reines, warmes Weiss mit feiner
   * Struktur; die dunkle Fassung ist ihr ruhiges Gegenstueck, damit die
   * Auswahl beim Wechsel nach Dunkel nicht grell wird.
   */
  {
    id: 'porcelain',
    name: 'Porzellan',
    group: 'white',
    light: {
      bg: '#fdfcfa',
      art: [
        'radial-gradient(ellipse 120% 80% at 50% -10%, #ffffff, transparent 70%)',
        'radial-gradient(ellipse 90% 60% at 50% 120%, rgba(200,195,185,0.12), transparent 70%)',
        grain(0.018),
      ].join(', '),
      surface: '#ffffff',
      surface2: '#ffffff',
      border: '#ebe8e2',
      rule: '#efece6',
      themeColor: '#fdfcfa',
    },
    dark: {
      bg: '#1a1a1a',
      art: ['radial-gradient(ellipse 120% 80% at 50% -10%, rgba(255,255,255,0.06), transparent 70%)', grain(0.03, '#ffffff')].join(', '),
      surface: '#222222',
      surface2: '#2a2a2a',
      border: '#353535',
      rule: '#2c2c2c',
      themeColor: '#1a1a1a',
    },
  },
  {
    id: 'deckle',
    name: 'Büttenpapier',
    group: 'white',
    light: {
      bg: '#fbf9f4',
      art: [
        'radial-gradient(ellipse 120% 90% at 50% 40%, transparent 60%, rgba(170,150,120,0.08))',
        'repeating-linear-gradient(90deg, rgba(120,100,70,0.035) 0 1px, transparent 1px 26px)',
        'repeating-linear-gradient(0deg, rgba(120,100,70,0.02) 0 1px, transparent 1px 3px)',
        grain(0.05, '#000000', 0.75),
      ].join(', '),
      surface: '#fefdfa',
      surface2: '#ffffff',
      border: '#e8e1d4',
      rule: '#ebe4d8',
      themeColor: '#fbf9f4',
    },
    dark: {
      bg: '#1d1b18',
      art: [
        'repeating-linear-gradient(90deg, rgba(255,245,225,0.025) 0 1px, transparent 1px 26px)',
        grain(0.04, '#ffffff', 0.75),
      ].join(', '),
      surface: '#25221e',
      surface2: '#2d2a25',
      border: '#3a352e',
      rule: '#2e2a24',
      themeColor: '#1d1b18',
    },
  },
  {
    id: 'carrara',
    name: 'Carrara',
    group: 'white',
    light: {
      bg: '#fafaf9',
      art: [marble('#8d9197', 0.28, '#d9d6d0', 0.12, 21), 'linear-gradient(160deg, #ffffff, #f3f2ef)'].join(', '),
      surface: '#ffffff',
      surface2: '#ffffff',
      border: '#e6e4e0',
      rule: '#eae8e4',
      themeColor: '#fafaf9',
    },
    dark: {
      bg: '#151516',
      art: [marble('#d8d4cc', 0.14, '#2c2c2f', 0.4, 21), 'linear-gradient(160deg, #1b1b1d, #101011)'].join(', '),
      surface: '#1d1d1f',
      surface2: '#252527',
      border: '#343437',
      rule: '#29292c',
      themeColor: '#151516',
    },
  },
  {
    id: 'silk',
    name: 'Seide',
    group: 'white',
    light: {
      bg: '#fcfbf8',
      art: [
        'linear-gradient(115deg, transparent 20%, rgba(255,255,255,0.9) 38%, transparent 55%)',
        'linear-gradient(115deg, transparent 50%, rgba(225,218,205,0.25) 68%, transparent 85%)',
        'radial-gradient(ellipse 100% 70% at 80% 0%, rgba(240,232,218,0.5), transparent 70%)',
        grain(0.015),
      ].join(', '),
      surface: '#ffffff',
      surface2: '#ffffff',
      border: '#ece7de',
      rule: '#efebe3',
      themeColor: '#fcfbf8',
    },
    dark: {
      bg: '#18171a',
      art: [
        'linear-gradient(115deg, transparent 20%, rgba(255,255,255,0.05) 38%, transparent 55%)',
        'linear-gradient(115deg, transparent 50%, rgba(200,190,220,0.05) 68%, transparent 85%)',
        grain(0.03, '#ffffff'),
      ].join(', '),
      surface: '#201f23',
      surface2: '#28272b',
      border: '#36343a',
      rule: '#2b2a2f',
      themeColor: '#18171a',
    },
  },
  {
    id: 'linen',
    name: 'Leinen',
    group: 'nature',
    light: {
      bg: '#ebe5da',
      art: [
        'radial-gradient(ellipse at 50% 0%, rgba(255,255,255,0.55), transparent 65%)',
        weave('rgba(90,70,40,A)', 0.07),
        grain(0.06),
      ].join(', '),
      surface: '#f4f0e8',
      surface2: '#faf8f3',
      border: '#d9cfbe',
      rule: '#d9cfbe',
      themeColor: '#ebe5da',
    },
    dark: {
      bg: '#1c1c1d',
      art: [
        'radial-gradient(ellipse at 50% 0%, rgba(255,255,255,0.05), transparent 60%)',
        weave('rgba(255,245,230,A)', 0.03),
        grain(0.035, '#ffffff'),
      ].join(', '),
      surface: '#242425',
      surface2: '#2c2c2e',
      border: '#38383a',
      rule: '#2f2f31',
      themeColor: '#1c1c1d',
    },
  },
  {
    id: 'marble',
    name: 'Marmor',
    group: 'nature',
    light: {
      bg: '#f1f0ed',
      art: [marble('#6f7378', 0.45, '#b9b6b0', 0.2, 4), 'linear-gradient(160deg, #f7f6f3, #e9e7e2)'].join(', '),
      surface: '#f8f7f5',
      surface2: '#fcfbfa',
      border: '#dcd9d3',
      rule: '#dedbd5',
      themeColor: '#f1f0ed',
    },
    dark: {
      bg: '#121213',
      art: [marble('#cfcac0', 0.2, '#2e2e31', 0.45, 9), 'linear-gradient(160deg, #19191b, #0d0d0e)'].join(', '),
      surface: '#1b1b1d',
      surface2: '#232326',
      border: '#333336',
      rule: '#28282b',
      themeColor: '#121213',
    },
  },
  {
    id: 'velvet',
    name: 'Samt',
    group: 'mood',
    light: {
      bg: '#eadcd9',
      art: [
        'radial-gradient(ellipse 120% 80% at 30% 10%, rgba(255,255,255,0.5), transparent 60%)',
        'radial-gradient(ellipse 90% 70% at 90% 100%, rgba(150,80,90,0.18), transparent 70%)',
        grain(0.05),
      ].join(', '),
      surface: '#f4ebe9',
      surface2: '#faf4f3',
      border: '#dbc7c3',
      rule: '#dcc9c5',
      themeColor: '#eadcd9',
    },
    dark: {
      bg: '#1d0a10',
      art: [
        'radial-gradient(ellipse 110% 70% at 30% 0%, rgba(150,40,62,0.45), transparent 65%)',
        'radial-gradient(ellipse 80% 60% at 100% 100%, rgba(90,20,36,0.5), transparent 70%)',
        grain(0.045, '#ffffff'),
      ].join(', '),
      surface: '#281117',
      surface2: '#32161e',
      border: '#4a2029',
      rule: '#3a1821',
      themeColor: '#1d0a10',
    },
  },
  {
    id: 'midnight',
    name: 'Nachtblau',
    group: 'mood',
    light: {
      bg: '#e4e9f0',
      art: [
        'radial-gradient(ellipse 120% 80% at 20% 0%, rgba(255,255,255,0.7), transparent 60%)',
        'radial-gradient(ellipse 90% 70% at 100% 100%, rgba(60,90,140,0.16), transparent 70%)',
        grain(0.04),
      ].join(', '),
      surface: '#eef2f7',
      surface2: '#f7f9fc',
      border: '#cdd6e2',
      rule: '#d2dae5',
      themeColor: '#e4e9f0',
    },
    dark: {
      bg: '#0a1020',
      art: [
        'radial-gradient(ellipse 110% 70% at 25% 0%, rgba(58,92,160,0.42), transparent 65%)',
        'radial-gradient(ellipse 80% 60% at 100% 100%, rgba(30,50,110,0.45), transparent 70%)',
        grain(0.035, '#ffffff'),
      ].join(', '),
      surface: '#111a2e',
      surface2: '#17223a',
      border: '#26324d',
      rule: '#1c2740',
      themeColor: '#0a1020',
    },
  },
  {
    id: 'spotlight',
    name: 'Bühnenlicht',
    group: 'mood',
    light: {
      bg: '#efe9df',
      art: [
        'radial-gradient(ellipse 70% 60% at 50% -10%, rgba(255,236,190,0.9), transparent 70%)',
        'radial-gradient(ellipse 100% 60% at 50% 110%, rgba(120,90,50,0.14), transparent 70%)',
        grain(0.04),
      ].join(', '),
      surface: '#f7f2ea',
      surface2: '#fbf8f3',
      border: '#ddd2c0',
      rule: '#e0d6c6',
      themeColor: '#efe9df',
    },
    dark: {
      bg: '#0c0b0a',
      art: [
        'radial-gradient(ellipse 60% 55% at 50% -12%, rgba(255,196,110,0.32), rgba(255,170,80,0.08) 55%, transparent 75%)',
        'radial-gradient(ellipse 120% 50% at 50% 115%, rgba(0,0,0,0.6), transparent 70%)',
        grain(0.04, '#ffffff'),
      ].join(', '),
      surface: '#171513',
      surface2: '#201d1a',
      border: '#322d27',
      rule: '#26221e',
      themeColor: '#0c0b0a',
    },
  },
  {
    id: 'slate',
    name: 'Schiefer',
    group: 'nature',
    light: {
      bg: '#e2e3e1',
      art: [
        'linear-gradient(170deg, rgba(255,255,255,0.45), transparent 55%)',
        grain(0.06, '#000000', 0.6),
      ].join(', '),
      surface: '#ecedeb',
      surface2: '#f5f6f4',
      border: '#cdcfcb',
      rule: '#d2d4d0',
      themeColor: '#e2e3e1',
    },
    dark: {
      bg: '#1f2327',
      art: [
        'linear-gradient(170deg, rgba(255,255,255,0.05), transparent 55%)',
        grain(0.05, '#ffffff', 0.6),
      ].join(', '),
      surface: '#272c31',
      surface2: '#2f353b',
      border: '#3a4148',
      rule: '#2e3439',
      themeColor: '#1f2327',
    },
  },
  {
    id: 'sage',
    name: 'Salbei',
    group: 'nature',
    light: {
      bg: '#dfe4da',
      art: [
        'radial-gradient(ellipse 120% 80% at 10% 0%, rgba(255,255,255,0.55), transparent 60%)',
        'radial-gradient(ellipse 90% 70% at 100% 100%, rgba(70,100,70,0.14), transparent 70%)',
        grain(0.05),
      ].join(', '),
      surface: '#eaeee6',
      surface2: '#f5f7f2',
      border: '#c9d1c2',
      rule: '#cfd6c8',
      themeColor: '#dfe4da',
    },
    dark: {
      bg: '#101813',
      art: [
        'radial-gradient(ellipse 110% 70% at 20% 0%, rgba(80,130,95,0.3), transparent 65%)',
        'radial-gradient(ellipse 80% 60% at 100% 100%, rgba(30,60,40,0.5), transparent 70%)',
        grain(0.035, '#ffffff'),
      ].join(', '),
      surface: '#17211b',
      surface2: '#1e2a23',
      border: '#2c3b31',
      rule: '#223028',
      themeColor: '#101813',
    },
  },
  {
    id: 'aurora',
    name: 'Aurora',
    group: 'mood',
    light: {
      bg: '#f1ebe6',
      art: [
        'radial-gradient(ellipse 70% 60% at 0% 0%, rgba(255,196,170,0.55), transparent 70%)',
        'radial-gradient(ellipse 70% 60% at 100% 10%, rgba(196,186,255,0.5), transparent 70%)',
        'radial-gradient(ellipse 80% 70% at 60% 100%, rgba(160,220,215,0.45), transparent 70%)',
        grain(0.04),
      ].join(', '),
      surface: '#f8f4f1',
      surface2: '#fcfaf8',
      border: '#e0d7d0',
      rule: '#e3dbd4',
      themeColor: '#f1ebe6',
    },
    dark: {
      bg: '#0d0f1a',
      art: [
        'radial-gradient(ellipse 70% 60% at 0% 0%, rgba(40,150,140,0.35), transparent 70%)',
        'radial-gradient(ellipse 70% 60% at 100% 5%, rgba(110,70,200,0.38), transparent 70%)',
        'radial-gradient(ellipse 80% 70% at 60% 105%, rgba(30,80,150,0.35), transparent 70%)',
        grain(0.035, '#ffffff'),
      ].join(', '),
      surface: '#151827',
      surface2: '#1c1f31',
      border: '#2a2e45',
      rule: '#20243a',
      themeColor: '#0d0f1a',
    },
  },
  pastel('blush', 'Rosé', { bg: '#f8ecec', a: 'rgba(244,190,196,0.55)', b: 'rgba(250,214,190,0.5)' }, { bg: '#1e1416', glow: 'rgba(190,110,125,0.3)' }),
  pastel('peach', 'Pfirsich', { bg: '#faeee4', a: 'rgba(252,200,165,0.55)', b: 'rgba(250,225,175,0.5)' }, { bg: '#1f1712', glow: 'rgba(200,130,85,0.28)' }),
  pastel('vanilla', 'Vanille', { bg: '#faf5e4', a: 'rgba(250,230,160,0.5)', b: 'rgba(240,215,190,0.45)' }, { bg: '#1c1912', glow: 'rgba(190,160,90,0.25)' }),
  pastel('mint', 'Minze', { bg: '#e9f5ee', a: 'rgba(170,225,200,0.55)', b: 'rgba(200,235,225,0.5)' }, { bg: '#111b17', glow: 'rgba(80,160,125,0.28)' }),
  pastel('sky', 'Himmel', { bg: '#e9f1f9', a: 'rgba(175,205,240,0.55)', b: 'rgba(205,225,245,0.5)' }, { bg: '#10161f', glow: 'rgba(85,125,190,0.3)' }),
  pastel('lavender', 'Lavendel', { bg: '#f0ecf8', a: 'rgba(205,190,240,0.55)', b: 'rgba(235,205,235,0.45)' }, { bg: '#18141f', glow: 'rgba(140,110,200,0.3)' }),
  pastel('cotton', 'Zuckerwatte', { bg: '#f5eef5', a: 'rgba(250,195,220,0.55)', b: 'rgba(185,215,250,0.55)' }, { bg: '#1a1520', glow: 'rgba(180,110,170,0.28)' }),
];

/* --------------------------------------------------------- Schriftfarben */

export const INKS: InkDef[] = [
  {
    id: 'ink',
    name: 'Tinte',
    light: { ink: '#14171c', soft: '#4d5360', faint: '#868b96' },
    dark: { ink: '#f6f4ef', soft: '#b9b6ae', faint: '#7c7a74' },
  },
  {
    id: 'graphite',
    name: 'Graphit',
    light: { ink: '#3b3e44', soft: '#666a72', faint: '#979ba2' },
    dark: { ink: '#cfd2d7', soft: '#9a9ea6', faint: '#6b6f76' },
  },
  {
    id: 'navy',
    name: 'Nachtblau',
    light: { ink: '#1b2c4d', soft: '#4d5d7c', faint: '#8894ab' },
    dark: { ink: '#d3def2', soft: '#9dabc6', faint: '#6a7690' },
  },
  {
    id: 'sepia',
    name: 'Sepia',
    light: { ink: '#4a3324', soft: '#77604f', faint: '#a8968a' },
    dark: { ink: '#eddcc6', soft: '#bba88f', faint: '#857563' },
  },
  {
    id: 'bordeaux',
    name: 'Bordeaux',
    light: { ink: '#551825', soft: '#814a55', faint: '#b0898f' },
    dark: { ink: '#f3d3d9', soft: '#c49da4', faint: '#8d6c72' },
  },
  {
    id: 'forest',
    name: 'Tannengrün',
    light: { ink: '#1c3a2d', soft: '#4c6a5c', faint: '#8aa197' },
    dark: { ink: '#d2e7da', soft: '#9db7a8', faint: '#6a8276' },
  },
  {
    id: 'champagne',
    name: 'Champagner',
    light: { ink: '#5e4718', soft: '#86714a', faint: '#b3a381' },
    dark: { ink: '#efdcaf', soft: '#c1ae83', faint: '#8a7b5c' },
  },
];

/* --------------------------------------------------------- Akkordfarben */

export const CHORDS: ColorDef[] = [
  { id: 'royal', name: 'Königsblau', light: '#1f4fa8', dark: '#8fb4ff' },
  { id: 'ruby', name: 'Rubin', light: '#a3122f', dark: '#ff8fa3' },
  { id: 'emerald', name: 'Smaragd', light: '#0d6a4e', dark: '#6fd8ad' },
  { id: 'amber', name: 'Bernstein', light: '#9a5300', dark: '#ffc266' },
  { id: 'violet', name: 'Violett', light: '#5b2a9a', dark: '#c6a8ff' },
  { id: 'text', name: 'Wie Schrift', light: 'var(--c-ink)', dark: 'var(--c-ink)' },
];

/* --------------------------------------------------------------- Anwenden */

export interface Appearance {
  backgroundId: string;
  inkId: string;
  chordId: string;
  /** Eigenes Hintergrundbild als Data-URL, gilt bei backgroundId "custom". */
  customImage?: string | null;
  /** Wie stark der Schleier ueber dem eigenen Bild ist, 0..1. */
  imageVeil?: number;
}

let current: Appearance = {
  backgroundId: THEME_DEFAULT,
  inkId: THEME_DEFAULT,
  chordId: THEME_DEFAULT,
  customImage: null,
  imageVeil: DEFAULT_IMAGE_VEIL,
};

/**
 * Schleier ueber dem eigenen Bild. Hell und Dunkel aendern das Bild bewusst
 * (fast) nicht – umschalten sollen die Bedienelemente darueber, nicht das
 * Foto. Der Schleier dunkelt nur gleichmaessig ab, so stark wie eingestellt
 * (0 = Bild pur); im dunklen Design kommt ein Hauch dazu.
 * Auch fuer die Vorschau-Kachel.
 */
export function customArt(image: string, tone: number, strength = DEFAULT_IMAGE_VEIL): string {
  const s = Math.min(1, Math.max(0, strength));
  const alpha = Math.min(0.9, s * 0.6 + tone * 0.06);
  const veil = `rgba(12, 12, 12, ${alpha.toFixed(3)})`;
  return `linear-gradient(${veil}, ${veil}), url("${image}")`;
}

/** Alle Variablen, die hier gesetzt werden koennen – zum Zuruecksetzen. */
const MANAGED = [
  '--c-bg',
  '--bg-art',
  '--c-surface',
  '--c-surface-2',
  '--c-elevated',
  '--c-border',
  '--c-rule',
  '--c-ink',
  '--c-ink-soft',
  '--c-ink-faint',
  '--c-chord',
  '--draw-ink',
  '--bg-size',
  '--bg-position',
  '--sidebar-bg',
  '--sidebar-blur',
];

export function findBackground(id: string): BackgroundDef | undefined {
  return BACKGROUNDS.find((b) => b.id === id);
}

export function currentAppearance(): Appearance {
  return current;
}

/** Merkt sich die Auswahl. Sichtbar wird sie mit dem naechsten applyTheme(). */
export function setAppearance(next: Partial<Appearance>): void {
  current = { ...current, ...next };
}

/** Nimmt alle hier gesetzten Variablen wieder weg. */
export function clearAppearance(): void {
  const style = document.documentElement.style;
  for (const name of MANAGED) style.removeProperty(name);
  document.documentElement.classList.remove('has-photo');
}

/** Motiv fuer eine Zwischenstufe: das naehere Motiv, ueberblendet mit der Mischfarbe. */
function blendArt(bg: BackgroundDef, tone: number, base: string): string {
  if (tone <= 0) return bg.light.art;
  if (tone >= 1) return bg.dark.art;
  const near = tone < 0.5 ? bg.light.art : bg.dark.art;
  // Je naeher an der Mitte, desto mehr deckt die Mischfarbe; so gibt es
  // beim Wechsel des Motivs keinen sichtbaren Sprung.
  const cover = Math.min(1, Math.abs(tone - (tone < 0.5 ? 0 : 1)) * 2);
  const veil = mixColor('transparent', base, cover);
  return `linear-gradient(${veil}, ${veil}), ${near}`;
}

/**
 * Setzt die Variablen fuer die gegebene Helligkeit: Flaechen gemischt
 * (0 hell … 1 dunkel), Schrift- und Akkordfarben von der Seite `side`.
 * Liefert die Statusleistenfarbe des Hintergrunds, falls einer gewaehlt ist.
 * Vorher clearAppearance() aufrufen (macht applyTheme).
 */
export function paintAppearance(tone: number, side: Tone): string | null {
  const style = document.documentElement.style;
  const mix = (pair: { light: string; dark: string }): string => mixColor(pair.light, pair.dark, tone);

  let themeColor: string | null = null;
  const bg = findBackground(current.backgroundId);
  if (bg) {
    const pick = (key: keyof BackgroundTone): string => mix({ light: bg.light[key], dark: bg.dark[key] });
    const base = pick('bg');
    style.setProperty('--c-bg', base);
    style.setProperty('--bg-art', blendArt(bg, tone, base));
    style.setProperty('--c-surface', pick('surface'));
    style.setProperty('--c-surface-2', pick('surface2'));
    style.setProperty('--c-elevated', pick('surface2'));
    style.setProperty('--c-border', pick('border'));
    style.setProperty('--c-rule', pick('rule'));
    themeColor = pick('themeColor');
  } else if (current.backgroundId === CUSTOM_BACKGROUND && current.customImage) {
    // Nur das Motiv tauschen; Karten und Leisten behalten die Theme-Farben
    // und bleiben damit ueber jedem Bild lesbar.
    style.setProperty('--bg-art', customArt(current.customImage, tone, current.imageVeil));
    // Text direkt auf dem Foto bekommt einen weichen Lichthof (base.css).
    document.documentElement.classList.add('has-photo');
    style.setProperty('--bg-size', 'cover');
    style.setProperty('--bg-position', 'center');
    // Seitenleiste als Glas: das Bild geht ueber die ganze Flaeche durch,
    // die Schrift darauf bleibt lesbar.
    style.setProperty('--sidebar-bg', 'color-mix(in srgb, var(--c-surface) 58%, transparent)');
    style.setProperty('--sidebar-blur', 'blur(22px) saturate(1.3)');
  }

  const ink = INKS.find((i) => i.id === current.inkId);
  if (ink) {
    const t = ink[side];
    style.setProperty('--c-ink', t.ink);
    style.setProperty('--c-ink-soft', t.soft);
    style.setProperty('--c-ink-faint', t.faint);
    style.setProperty('--draw-ink', t.ink);
  }

  const chord = CHORDS.find((c) => c.id === current.chordId);
  if (chord) style.setProperty('--c-chord', chord[side]);

  return themeColor;
}
